import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LanguageContext } from '../LanguageContext';
import { AuthContext } from '../AuthContext';
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble"; // Corrected import
import Avatar from "@cloudscape-design/chat-components/avatar"; // Corrected import
import ButtonGroup from "@cloudscape-design/components/button-group";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import ReactMarkdown from 'react-markdown';
import publicBuildInfo from '../generated/publicBuildInfo';
import { formatPublicBuildVersion } from '../utils/publicBuildVersion';

function Help() {
  const { language } = useContext(LanguageContext);
  const { user } = useContext(AuthContext); // Get user from AuthContext
  const navigate = useNavigate();
  const [chatVisible, setChatVisible] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promptValue, setPromptValue] = useState("");
  const chatContainerRef = useRef(null); // Reference to the chat container
  const maxPromptLength = 800;

  const text = {
    en: {
      title: "Help and Support",
      welcome: "This page provides support for common ISET portal questions, application steps, document preparation, and technical issues.",
      aiSupport: "AI support assistant",
      startChat: "Start Chat",
      closeChat: "Close Chat",
      questionLabel: "Your question",
      questionPlaceholder: "General support only. Do not include your Social Insurance Number, password, banking information, or other sensitive personal details.",
      charsRemaining: "characters remaining",
      send: "Send",
      transcriptLabel: "Conversation",
      responseLoading: "Generating a response...",
      faqs: "Frequently Asked Questions (FAQs)",
      faq1: "Who can apply for the ISET program?",
      faq2: "What documents may I be asked to provide?",
      faq3: "How do I find an Indigenous service delivery organization?",
      appointmentIssues: "Portal or application issues",
      appointmentHint: "If you are having trouble using the portal or sending your application, try the following:",
      step1: "Ensure your browser is up to date.",
      step2: "Clear your browser cache and cookies.",
      step3: "Try accessing the portal from a different device or browser.",
      contactUs: "If the issue continues, sign in and use <a href='/messages/new' class='govuk-link'>Messages</a> to contact the program team, or reach out to your service delivery organization if one is already supporting you.",
      officialResources: "Official ISET Program Resources",
      officialHint: "For program information from NWAC and the Government of Canada, visit:",
      resource1: "NWAC – ISET Program Overview",
      resource2: "Government of Canada – ISET Program Details",
      resource3: "Government of Canada – Indigenous organizations delivering ISET",
      backButton: "Back",
      versionLabel: "Version",
    },
    fr: {
      title: "Aide et soutien",
      welcome: "Cette page offre du soutien pour les questions courantes sur le portail ISET, les étapes de la demande, la préparation des documents et les problèmes techniques.",
      aiSupport: "Assistant de soutien IA",
      startChat: "Démarrer le clavardage",
      closeChat: "Fermer le clavardage",
      questionLabel: "Votre question",
      questionPlaceholder: "Soutien général seulement. N’inscrivez pas votre numéro d’assurance sociale, votre mot de passe, vos renseignements bancaires ni d’autres renseignements personnels sensibles.",
      charsRemaining: "caractères restants",
      send: "Envoyer",
      transcriptLabel: "Conversation",
      responseLoading: "Génération d'une réponse...",
      faqs: "Questions fréquentes (FAQ)",
      faq1: "Qui peut présenter une demande au programme ISET?",
      faq2: "Quels documents pourrais-je devoir fournir?",
      faq3: "Comment trouver un organisme autochtone de prestation ISET?",
      appointmentIssues: "Problèmes liés au portail ou à la demande",
      appointmentHint: "Si vous éprouvez des difficultés à utiliser le portail ou à envoyer votre demande, essayez ce qui suit :",
      step1: "Assurez-vous que votre navigateur est à jour.",
      step2: "Effacez le cache et les cookies de votre navigateur.",
      step3: "Essayez d'accéder au portail à partir d'un autre appareil ou navigateur.",
      contactUs: "Si le problème persiste, ouvrez une session et utilisez les <a href='/messages/new' class='govuk-link'>Messages</a> pour joindre l'équipe du programme, ou communiquez avec votre organisme de prestation si un organisme vous accompagne déjà.",
      officialResources: "Ressources officielles du programme ISET",
      officialHint: "Pour obtenir de l’information du programme auprès de l’AFAC et du gouvernement du Canada, consultez :",
      resource1: "AFAC – Aperçu du programme ISET",
      resource2: "Gouvernement du Canada – Détails sur le programme ISET",
      resource3: "Gouvernement du Canada – Organismes autochtones offrant ISET",
      backButton: "Retour",
      versionLabel: "Version",
    },
  };

  const versionStamp = formatPublicBuildVersion(publicBuildInfo, text[language].versionLabel);

  function getInitials(name) {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const handleSendMessage = async () => {
    if (promptValue.trim() && !loading) {
      const trimmedPrompt = promptValue.trim().slice(0, maxPromptLength);
      const history = chatMessages
        .slice(-8)
        .map((message) => ({
          role: message.type === "incoming" ? "assistant" : "user",
          content: String(message.text || ""),
        }));
      const userMessage = {
        type: "outgoing",
        text: trimmedPrompt,
        timestamp: new Date().toLocaleTimeString(),
      };
      setChatMessages((prev) => [...prev, userMessage]);
      setPromptValue("");
      setLoading(true);

      try {
        const response = await fetch("/api/ai-support", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ prompt: trimmedPrompt, language, history })
        });

        const data = await response.json().catch(() => ({}));
        const aiReply = data.reply;

        setChatMessages((prev) => [
          ...prev,
          {
            type: "incoming",
            text:
              aiReply ||
              (language === "fr"
                ? "Désolé, je n’ai pas compris. Veuillez reformuler votre question."
                : "Sorry, I did not understand that. Please try asking in a different way."),
            timestamp: new Date().toLocaleTimeString(),
          }
        ]);
      } catch (err) {
        console.error("AI error:", err);
        setChatMessages((prev) => [
          ...prev,
          {
            type: "incoming",
            text:
              language === "fr"
                ? "Un problème est survenu. Veuillez réessayer plus tard."
                : "Something went wrong. Please try again later.",
            timestamp: new Date().toLocaleTimeString(),
          }
        ]);
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; // Scroll to the bottom
    }
  }, [chatMessages]); // Trigger scrolling whenever chatMessages changes

  return (
    <main className="govuk-main-wrapper govuk-width-container" role="main">
      <h1 className="govuk-heading-l">{text[language].title}</h1>
      <p className="govuk-body">{text[language].welcome}</p>

      {/* AI Support Section */}
      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible app-surface-panel app-ai-support">
        <div className="app-ai-support__header">
          <div className="app-ai-support__header-copy">
            <h2 className="govuk-heading-m govuk-!-margin-bottom-0">{text[language].aiSupport}</h2>
          </div>
          <button
            className="govuk-button govuk-!-margin-bottom-0 app-ai-support__toggle"
            onClick={() => setChatVisible(!chatVisible)}
          >
            {chatVisible ? text[language].closeChat : text[language].startChat}
          </button>
        </div>
        {chatVisible && (
          <div className="app-ai-support__workspace">
            <div
              ref={chatContainerRef}
              className="app-ai-support__transcript"
              aria-label={text[language].transcriptLabel}
            >
              {chatMessages.map((message, index) => (
                <ChatBubble
                  key={index}
                  ariaLabel={`${message.type === "outgoing" ? "You" : "AI"} at ${message.timestamp}`}
                  type={message.type}
                  avatar={
                    message.type === "outgoing" ? (
                      user && user.fullName ? (
                        <Avatar
                          ariaLabel={user.fullName}
                          tooltipText={user.fullName}
                          initials={getInitials(user.fullName).slice(0,2)}
                        />
                      ) : (
                        <Avatar
                          ariaLabel="You"
                          tooltipText="You"
                          iconName="user-profile"
                        />
                      )
                    ) : (
                      <Avatar
                        color="gen-ai"
                        iconName="gen-ai"
                        ariaLabel="Generative AI assistant"
                        tooltipText="Generative AI assistant"
                      />
                    )
                  }
                  actions={
                    message.type === "incoming" && (
                      <ButtonGroup
                        ariaLabel="Chat bubble actions"
                        variant="icon"
                        items={[
                          {
                            type: "group",
                            text: "Feedback",
                            items: [
                              {
                                type: "icon-button",
                                id: "helpful",
                                iconName: "thumbs-up-filled",
                                text: "Helpful.",
                                disabled: true,
                                disabledReason: "“Helpful” feedback has been submitted.",
                              },
                              {
                                type: "icon-button",
                                id: "not-helpful",
                                iconName: "thumbs-down",
                                text: "Not helpful",
                                disabled: true,
                                disabledReason: "“Not helpful” option is unavailable after “helpful” feedback submitted.",
                              },
                            ],
                          },
                          {
                            type: "icon-button",
                            id: "copy",
                            iconName: "copy",
                            text: "Copy",
                            popoverFeedback: (
                              <StatusIndicator type="success">
                                Message copied
                              </StatusIndicator>
                            ),
                          },
                        ]}
                      />
                    )
                  }
                >
                  {message.type === "incoming" ? (
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  ) : (
                    message.text
                  )}
                </ChatBubble>
              ))}
              {loading && <p className="govuk-hint govuk-!-margin-bottom-0">{text[language].responseLoading}</p>}
            </div>
            <div className="app-ai-support__composer">
              <label className="govuk-label govuk-!-margin-bottom-1" htmlFor="ai-support-question">
                {text[language].questionLabel}
              </label>
              <textarea
                id="ai-support-question"
                className="govuk-textarea app-ai-support__textarea"
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={text[language].questionPlaceholder}
                rows={3}
                maxLength={maxPromptLength}
                disabled={loading}
              />
              <div className="app-ai-support__composer-footer">
                <div className="govuk-hint govuk-!-margin-bottom-0">
                  {maxPromptLength - promptValue.length} {text[language].charsRemaining}
                </div>
                <button onClick={handleSendMessage} className="govuk-button govuk-!-margin-bottom-0" disabled={loading || !promptValue.trim()}>
                  {text[language].send}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* FAQs Section */}
      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].faqs}</h2>
        <ul className="govuk-list govuk-list--bullet">
  <li>
    <a href="https://www.nwac.ca/programs/iset/" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].faq1}
    </a>
  </li>
  <li>
    <a href="https://www.canada.ca/en/employment-social-development/programs/indigenous-skills-employment-training.html" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].faq2}
    </a>
  </li>
  <li>
    <a href="https://www.canada.ca/en/employment-social-development/programs/indigenous-skills-employment-training/service-delivery-organizations.html" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].faq3}
    </a>
  </li>
</ul>
      </section>

      {/* Appointment Issues Section */}
      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].appointmentIssues}</h2>
        <p className="govuk-body">{text[language].appointmentHint}</p>
        <ul className="govuk-list govuk-list--bullet">
          <li>{text[language].step1}</li>
          <li>{text[language].step2}</li>
          <li>{text[language].step3}</li>
        </ul>
        <p className="govuk-body" dangerouslySetInnerHTML={{ __html: text[language].contactUs }}></p>
      </section>

      {/* Official Resources Section */}
      <section className="govuk-section-break govuk-section-break--xl govuk-section-break--visible">
        <h2 className="govuk-heading-m">{text[language].officialResources}</h2>
        <p className="govuk-body">{text[language].officialHint}</p>
        <ul className="govuk-list govuk-list--bullet">
  <li>
    <a href="https://www.nwac.ca/programs/iset/" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].resource1}
    </a>
  </li>
  <li>
    <a href="https://www.canada.ca/en/employment-social-development/programs/indigenous-skills-employment-training.html" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].resource2}
    </a>
  </li>
  <li>
    <a href="https://www.canada.ca/en/employment-social-development/programs/indigenous-skills-employment-training/service-delivery-organizations.html" target="_blank" rel="noreferrer" className="govuk-link">
      {text[language].resource3}
    </a>
  </li>
</ul>
      </section>

      <p className="govuk-hint govuk-!-margin-top-7 govuk-!-margin-bottom-0">
        {versionStamp}
      </p>

      <div className="govuk-button-group">
        <button className="govuk-button govuk-button--secondary" onClick={() => navigate(-1)}>
          {text[language].backButton}
        </button>
      </div>
    </main>
  );
}

export default Help;
